import React from 'react';
import {
    Box,
    Paper,
    Typography,
    Divider,
    Skeleton,
    Tooltip,
    Chip
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    CheckCircle as GoodIcon,
    Warning as WarningIcon,
    Error as CriticalIcon
} from '@mui/icons-material';

/**
 * Executive Summary Bar Component
 * 
 * Displays key financial health indicators at a glance in a horizontal bar.
 * This is the first thing executives see - critical metrics with traffic light status.
 * 
 * Follows PSA industry standards (Certinia, Kantata, BigTime pattern):
 * - Revenue with trend
 * - Profit with trend
 * - Utilization vs target
 * - DSO (Days Sales Outstanding)
 * - Portfolio Health Score
 * 
 * @param {Object} props
 * @param {Object} props.metrics - Metric values
 * @param {boolean} props.loading - Loading state
 * @param {Function} props.onMetricClick - Click handler for drill-down
 */
const ExecutiveSummaryBar = ({
    metrics = {},
    loading = false,
    onMetricClick = null,
    maxMetrics = 5,
    metricKeys = null,
    testId = 'financial-executive-summary',
}) => {
    // Format currency with compact notation for large values
    const formatCurrency = (value) => {
        if (value === null || value === undefined) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            notation: Math.abs(value) >= 1000000 ? 'compact' : 'standard',
            maximumFractionDigits: Math.abs(value) >= 1000 ? 0 : 2
        }).format(value);
    };

    // Format percentage
    const formatPercentage = (value) => {
        if (value === null || value === undefined) return 'N/A';
        // KPI percentages are expected to be pre-scaled by backend contracts.
        const percentageValue = Number(value);
        if (!Number.isFinite(percentageValue)) {
            return 'N/A';
        }
        return `${percentageValue.toFixed(0)}%`;
    };

    // Get status indicator
    const getStatusIcon = (status) => {
        switch (status) {
            case 'good': return <GoodIcon sx={{ color: 'success.main', fontSize: 14 }} />;
            case 'warning': return <WarningIcon sx={{ color: 'warning.main', fontSize: 14 }} />;
            case 'critical': return <CriticalIcon sx={{ color: 'error.main', fontSize: 14 }} />;
            default: return null;
        }
    };

    // Get trend indicator
    const getTrendIndicator = (trend, isPositiveGood = true) => {
        if (!trend || trend === 0) return null;

        const isPositive = trend > 0;
        const isGood = isPositiveGood ? isPositive : !isPositive;
        const color = isGood ? 'success.main' : 'error.main';
        const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;

        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Icon sx={{ color, fontSize: 12 }} />
                <Typography variant="caption" sx={{ color, fontWeight: 'bold', fontSize: '0.65rem' }}>
                    {isPositive ? '+' : ''}{trend.toFixed(0)}%
                </Typography>
            </Box>
        );
    };

    // Determine status based on metric thresholds
    const getUtilizationStatus = (util, target) => {
        if (util === null || util === undefined || target === null || target === undefined) {
            return 'warning';
        }
        if (util >= target) return 'good';
        return 'warning';
    };

    const getDSOStatus = (dso) => {
        if (dso <= 35) return 'good';
        if (dso <= 45) return 'warning';
        return 'critical';
    };

    const getHealthStatus = (score) => {
        if (score === null || score === undefined || !Number.isFinite(Number(score))) {
            return 'warning';
        }
        const numericScore = Number(score);
        if (numericScore >= 80) return 'good';
        if (numericScore >= 50) return 'warning';
        return 'warning';
    };

    const getAtRiskStatus = (count) => {
        const numeric = Number(count);
        if (!Number.isFinite(numeric) || numeric <= 0) {
            return 'good';
        }
        if (numeric >= 3) {
            return 'critical';
        }
        return 'warning';
    };

    // Summary metrics configuration
    const summaryMetrics = [
        ...(metrics.projectsAtRisk !== undefined && metrics.projectsAtRisk !== null
            ? [{
                key: 'atRisk',
                label: 'At risk',
                value: metrics.projectsAtRisk,
                format: (value) => {
                    const count = Number(value);
                    if (!Number.isFinite(count)) {
                        return 'N/A';
                    }
                    return `${count} project${count === 1 ? '' : 's'}`;
                },
                status: getAtRiskStatus(metrics.projectsAtRisk),
                subValue: 'high-severity alerts',
                tooltip: 'Projects with high-severity portfolio attention signals',
                isPositiveGood: false,
            }]
            : []),
        {
            key: 'revenue',
            label: 'Revenue',
            value: metrics.totalRevenue,
            format: formatCurrency,
            trend: metrics.revenueTrend,
            trendLabel: 'vs prior',
            isPositiveGood: true,
            tooltip: 'Total recognized revenue for the period'
        },
        {
            key: 'profit',
            label: 'Profit',
            value: metrics.netProfit,
            format: formatCurrency,
            trend: metrics.profitTrend,
            trendLabel: 'vs prior',
            isPositiveGood: true,
            tooltip: 'Net profit after all costs'
        },
        {
            key: 'utilization',
            label: 'Utilization',
            value: metrics.utilization,
            format: formatPercentage,
            status: getUtilizationStatus(
                metrics.utilization,
                metrics.utilizationTarget
            ),
            subValue: metrics.utilizationTarget
                ? `vs ${formatPercentage(metrics.utilizationTarget)} target`
                : 'vs target N/A',
            tooltip: 'Billable hours / Available hours'
        },
        {
            key: 'dso',
            label: 'DSO',
            value: metrics.dso,
            format: (v) => {
                const numeric = Number(v);
                if (!Number.isFinite(numeric)) {
                    return 'N/A';
                }
                return `${Math.round(numeric)}d`;
            },
            status: getDSOStatus(metrics.dso),
            subValue: metrics.dsoPrior ? `vs ${Math.round(metrics.dsoPrior)}d prior` : null,
            tooltip: 'Days Sales Outstanding - Average collection time',
            isPositiveGood: false
        },
        {
            key: 'health',
            label: 'Health',
            value: metrics.healthScore,
            // AGENT_DONE: treat 0 as a real score — truthy check previously rendered Health as N/A
            format: (v) => {
                const numeric = Number(v);
                if (!Number.isFinite(numeric)) {
                    return 'N/A';
                }
                return `${Math.round(numeric)}`;
            },
            status: getHealthStatus(metrics.healthScore),
            subValue: '/100',
            tooltip: 'Portfolio health score based on profitability and risk'
        }
    ];

    const orderedMetrics = Array.isArray(metricKeys) && metricKeys.length > 0
        ? metricKeys
            .map((key) => summaryMetrics.find((metric) => metric.key === key))
            .filter(Boolean)
        : summaryMetrics;

    const visibleMetrics = orderedMetrics.slice(0, Math.max(1, maxMetrics));

    if (loading) {
        return (
            <Paper sx={{ p: 1.5, mb: 2 }} data-testid={testId}>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', gap: 2 }}>
                    {[1, 2, 3, 4, 5].map((i) => (
                        <Box key={i} sx={{ textAlign: 'center', flex: 1 }}>
                            <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
                            <Skeleton variant="text" width="80%" height={32} sx={{ mx: 'auto' }} />
                            <Skeleton variant="text" width="50%" sx={{ mx: 'auto' }} />
                        </Box>
                    ))}
                </Box>
            </Paper>
        );
    }

    return (
        <Paper
            data-testid={testId}
            sx={{
                p: 1.5,
                mb: 2,
                background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
                borderTop: '4px solid',
                borderColor: 'primary.main',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    alignItems: 'stretch',
                    gap: 1,
                    flexWrap: { xs: 'wrap', md: 'nowrap' }
                }}
            >
                {visibleMetrics.map((metric, index) => (
                    <React.Fragment key={metric.key}>
                        {index > 0 && (
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ display: { xs: 'none', md: 'block' } }}
                            />
                        )}
                        <Tooltip title={metric.tooltip} arrow placement="top">
                            <Box
                                onClick={() => onMetricClick?.(metric.key)}
                                sx={{
                                    textAlign: 'center',
                                    flex: 1,
                                    minWidth: { xs: '45%', sm: '30%', md: 'auto' },
                                    py: 0.5,
                                    px: 1,
                                    cursor: onMetricClick ? 'pointer' : 'default',
                                    borderRadius: 1,
                                    transition: 'background-color 0.2s',
                                    '&:hover': onMetricClick ? {
                                        bgcolor: 'action.hover'
                                    } : {}
                                }}
                            >
                                {/* Label */}
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        fontSize: '0.65rem'
                                    }}
                                >
                                    {metric.label}
                                    {metric.status && getStatusIcon(metric.status)}
                                </Typography>

                                {/* Value */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        my: 0.25
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: '1.1rem',
                                            lineHeight: 1.2
                                        }}
                                    >
                                        {metric.format(metric.value)}
                                    </Typography>
                                    {metric.subValue && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontSize: '0.6rem' }}
                                        >
                                            {metric.subValue}
                                        </Typography>
                                    )}
                                </Box>

                                {/* Trend */}
                                {metric.trend !== undefined && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                        {getTrendIndicator(metric.trend, metric.isPositiveGood)}
                                    </Box>
                                )}

                                {/* Status chip for certain metrics */}
                                {metric.status && !metric.trend && (
                                    <Chip
                                        size="small"
                                        label={metric.status === 'good' ? '✓' : metric.status === 'warning' ? '!' : '✗'}
                                        sx={{
                                            height: 16,
                                            fontSize: '0.6rem',
                                            bgcolor: `${metric.status}.light`,
                                            color: `${metric.status}.main`,
                                            '& .MuiChip-label': { px: 0.75 }
                                        }}
                                    />
                                )}
                            </Box>
                        </Tooltip>
                    </React.Fragment>
                ))}
            </Box>
        </Paper>
    );
};

export default ExecutiveSummaryBar;

